import * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@swyftx/aviary/atoms/Card';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Notification } from '@swyftx/aviary/atoms/Notification';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselUnstyledNext,
  CarouselUnstyledPrevious,
} from '@swyftx/aviary/atoms/ShadcnCarousel';
import { Body, Heading, Numeric } from '@swyftx/aviary/atoms/Typography';
import { useTailwindBreakpoint } from '@swyftx/aviary/hooks/useTailwindBreakpoint';
import { TickInCircle } from '@swyftx/aviary/icons/outlined';
import { LoanHealthGauge } from '@swyftx/features/loans/components/LoanHealth/LoanGuageGraphic/LoanHealthGauge';
import { LOAN_SUMMARY_DATA } from '@swyftx/features/loans/components/LoanHealth/api/data/loanHealth.data';

import { HealthStatus } from '../api/types/loanHealth.types';

type Props = {
  className?: string;
};

const LoanHealthCarousel: React.FC<Props> = () => {
  const isXs = useTailwindBreakpoint('xs');
  const { t } = useTranslation('loans');

  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) {
      return;
    }

    setCurrent(api.selectedScrollSnap());

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  const rowItem = (title: string, value: string): JSX.Element => (
    <FlexLayout direction='row' justifyContent='space-between' spacing={4}>
      <Body size='medium' color='secondary'>
        {title}
      </Body>
      <Numeric size='medium' weight='emphasis'>
        {value}
      </Numeric>
    </FlexLayout>
  );

  return (
    <FlexLayout direction='column' className=' w-full' spacing={24} justifyContent='center'>
      <Carousel setApi={setApi}>
        <FlexLayout direction='row' alignItems='center' spacing={24}>
          {!isXs && <CarouselUnstyledPrevious size='lg' variant='filled' color='subtle' />}

          <CarouselContent>
            {(Object.keys(LOAN_SUMMARY_DATA) as HealthStatus[]).map((key) => {
              const summaryData = LOAN_SUMMARY_DATA[key];
              if (!summaryData) return;

              const isSuccessCard = summaryData.cardSeverity === 'success';

              return (
                <CarouselItem key={key}>
                  <FlexLayout direction='column' alignItems='center' spacing={24}>
                    <LoanHealthGauge id={key} healthPercentage={summaryData.percentage} size={isXs ? 'sm' : 'lg'} />

                    <Card className='w-full rounded-12 bg-color-background-surface-secondary p-16' border={false}>
                      <FlexLayout direction='column' spacing={8}>
                        <Heading size='h5'>{t('loanHealth.loanSummaryData.title')}</Heading>
                        {rowItem(
                          t('loanHealth.loanSummaryData.loanAmount'),
                          t('loanHealth.loanSummaryData.loanAmountVal'),
                        )}
                        {rowItem(
                          t('loanHealth.loanSummaryData.currenBTCPrice'),
                          t(summaryData.btcPriceSecurityValue as unknown as TemplateStringsArray),
                        )}
                        {rowItem(
                          t('loanHealth.loanSummaryData.securityAmount'),
                          t('loanHealth.loanSummaryData.currenBTCPriceVal'),
                        )}
                        {rowItem(
                          t('loanHealth.loanSummaryData.securityValue'),
                          t(summaryData.btcPriceSecurityValue as unknown as TemplateStringsArray),
                        )}
                        {rowItem(
                          t('loanHealth.loanSummaryData.lvr'),
                          t(summaryData.lvr as unknown as TemplateStringsArray),
                        )}
                      </FlexLayout>
                    </Card>

                    <Notification
                      severity={summaryData.cardSeverity === 'success' ? undefined : summaryData.cardSeverity}
                      title={t(summaryData.lvrTitle as unknown as TemplateStringsArray)}
                      className={
                        isSuccessCard
                          ? ' mb-16 border-color-border-success bg-color-background-success-subtle'
                          : 'mb-16'
                      }
                      icon={
                        isSuccessCard ? <TickInCircle className='min-w-[24px] text-color-icon-success' /> : undefined
                      }
                    >
                      <Body>{t(summaryData.lvrDescription as unknown as TemplateStringsArray)}</Body>
                    </Notification>
                  </FlexLayout>
                </CarouselItem>
              );
            })}
          </CarouselContent>

          {!isXs && <CarouselUnstyledNext size='lg' variant='filled' color='subtle' />}
        </FlexLayout>

        <FlexLayout direction='row' justifyContent='space-between' alignItems='center' className='w-full'>
          {isXs && <CarouselUnstyledPrevious size='lg' variant='filled' color='subtle' />}

          <FlexLayout spacing={8} justifyContent='center' alignItems='center' className='w-full'>
            {Object.keys(LOAN_SUMMARY_DATA).map((_, index) => (
              <div
                key={index}
                className={`h-8 w-8 cursor-pointer rounded-50 transition-colors ${
                  current === index ? 'bg-color-text-primary' : 'bg-color-text-placeholder'
                }`}
                // Allow clicking dots to navigate
                onClick={() => api?.scrollTo(index)}
              />
            ))}
          </FlexLayout>

          {isXs && <CarouselUnstyledNext size='lg' variant='filled' color='subtle' />}
        </FlexLayout>
      </Carousel>
    </FlexLayout>
  );
};

LoanHealthCarousel.displayName = 'LoansFAQ';

export { LoanHealthCarousel };
